import { useState } from "react";
import { useNavigate } from "react-router";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  PARTNER_STATUSES,
  PARTNER_STATUS_LABEL,
  PARTNER_TIERS,
  PARTNER_TIER_LABEL,
  PARTNER_TYPES,
  PARTNER_TYPE_LABEL,
  type PartnerStatus,
  type PartnerTier,
  type PartnerType,
} from "@colab/shared";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreatePartner } from "@/features/partners/use-create-partner";

// Phase 7 sub-bucket 10 — admin create partner.
//
// Simplified vs the legacy CreatePartnerSheet: the "Browse HubSpot" prefill
// tab is deferred (still needs a SPA port of the candidate-search UI; the
// `useHubspotPartnerCandidates` hook already exists from Phase 6 and will
// power that tab in a follow-up). Manual entry covers the primary flow.
export function CreatePartnerSheet({
  variant = "default",
}: {
  variant?: "default" | "empty";
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const createPartner = useCreatePartner();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const input = {
      company_name: String(fd.get("company_name") ?? "").trim(),
      partner_type: String(fd.get("partner_type") ?? "referral_partner") as PartnerType,
      tier: String(fd.get("tier") ?? "bronze") as PartnerTier,
      status: String(fd.get("status") ?? "active") as PartnerStatus,
      primary_contact_name: emptyToNull(fd.get("primary_contact_name")),
      primary_contact_email: emptyToNull(fd.get("primary_contact_email")),
      region: emptyToNull(fd.get("region")),
      website: emptyToNull(fd.get("website")),
      assigned_owner: emptyToNull(fd.get("assigned_owner")),
      notes: emptyToNull(fd.get("notes")),
      // HubSpot link is only set when creating via the deferred Browse-HubSpot
      // tab; manual entry leaves it null and a follow-up Sync HubSpot mints
      // the record.
      hubspot_partner_record_id: null,
    };
    const result = await createPartner.mutateAsync(input);
    toast.success("Partner created");
    setOpen(false);
    navigate(`/admin/partners/${result.id}`);
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {variant === "empty" ? (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add first partner
          </Button>
        ) : (
          <Button size="sm">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add partner
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight">
            New partner
          </SheetTitle>
          <SheetDescription>
            Enter the partner details. You can sync to HubSpot afterward.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <Field label="Company name" name="company_name" required />

          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Partner type"
              name="partner_type"
              required
              options={PARTNER_TYPES.map((t) => ({
                value: t,
                label: PARTNER_TYPE_LABEL[t],
              }))}
              defaultValue="referral_partner"
            />
            <SelectField
              label="Tier"
              name="tier"
              required
              options={PARTNER_TIERS.map((t) => ({
                value: t,
                label: PARTNER_TIER_LABEL[t],
              }))}
              defaultValue="bronze"
            />
          </div>

          <SelectField
            label="Status"
            name="status"
            required
            options={PARTNER_STATUSES.map((s) => ({
              value: s,
              label: PARTNER_STATUS_LABEL[s],
            }))}
            defaultValue="active"
          />

          <div className="grid grid-cols-2 gap-3">
            <Field label="Primary contact name" name="primary_contact_name" />
            <Field label="Region" name="region" placeholder="EMEA" />
          </div>

          <Field
            label="Primary contact email"
            name="primary_contact_email"
            type="email"
          />

          <Field label="Website" name="website" type="url" placeholder="https://" />
          <Field label="Assigned Bookboost owner" name="assigned_owner" />

          <div className="space-y-1.5">
            <Label htmlFor="notes" className="text-xs">
              Internal notes
            </Label>
            <Textarea id="notes" name="notes" rows={3} />
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={createPartner.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createPartner.isPending}>
              {createPartner.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create partner
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function emptyToNull(value: FormDataEntryValue | null): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function Field({
  label,
  name,
  type = "text",
  required = false,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={name} className="text-xs">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      <Input
        id={name}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
      />
    </div>
  );
}

function SelectField({
  label,
  name,
  required,
  options,
  defaultValue,
}: {
  label: string;
  name: string;
  required?: boolean;
  options: { value: string; label: string }[];
  defaultValue?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={name} className="text-xs">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      <select
        id={name}
        name={name}
        defaultValue={defaultValue}
        required={required}
        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
